import React from "react";
import { useForm, FormProvider } from "react-hook-form";

import { FieldErrors } from "src/api/types";
import { RuleNodeV2, RuleNodeV2Data } from "src/constants/NodeDataTypes";
import { NodeEditorBaseProps } from "src/nodeEditor/NodeEditor";
import { useFormApplyNodeSuggestion } from "src/nodeEditor/applyNodeSuggestion";
import { AddFieldsCard } from "src/ruleNodeV2Editor/AddFieldsCard";
import { Branches } from "src/ruleNodeV2Editor/Branches";
import { DefaultValuesCard } from "src/ruleNodeV2Editor/DefaultValuesCard";
import { RuleNodeV2Form } from "src/ruleNodeV2Editor/types";
import { convertFieldErrorsBeToFe } from "src/utils/FieldErrorUtils";
import { useSubmitForm } from "src/utils/useSubmitForm";

type Props = {
  selectedNode: RuleNodeV2;
  lazyInputs: boolean;
} & NodeEditorBaseProps<RuleNodeV2Data>;

export const RuleV2Editor: React.FC<Props> = ({
  selectedNode,
  immutable,
  onUpdate,
  isReactive,
  displayedError,
  hideActionButtons,
  lazyInputs,
}) => {
  const formMethods = useForm<RuleNodeV2Form>({
    defaultValues: selectedNode.data,
    ...(isReactive && { values: selectedNode.data }),
  });

  useFormApplyNodeSuggestion<RuleNodeV2Form>(selectedNode.id, (suggestion) => {
    formMethods.setValue("default_effects", suggestion.default_effects);
    formMethods.setValue("branches", suggestion.branches);
  });

  useSubmitForm({
    onChange: (newValues) => onUpdate({ newData: newValues }),
    disabled: isReactive,
    previousValues: selectedNode.data,
    watch: formMethods.watch,
  });

  const fieldErrors: FieldErrors | undefined = displayedError?.field_errors
    ? convertFieldErrorsBeToFe(displayedError.field_errors, true)
    : undefined;

  return (
    <FormProvider {...formMethods}>
      <div className="space-y-5">
        <AddFieldsCard
          fieldErrors={fieldErrors}
          hideActionButtons={hideActionButtons}
          immutable={immutable}
          lazyInputs={lazyInputs}
        />
        <Branches
          fieldErrors={fieldErrors}
          hideActionButtons={hideActionButtons}
          immutable={immutable}
          lazyInputs={lazyInputs}
        />
        <DefaultValuesCard
          fieldErrors={fieldErrors}
          immutable={immutable}
          lazyInputs={lazyInputs}
        />
      </div>
    </FormProvider>
  );
};
